import React, { useState, useEffect } from 'react';

interface VideoPlayerProps {
  url: string;
  className?: string;
  poster?: string;
  onLoadStart?: () => void;
  onCanPlay?: () => void;
  onError?: () => void;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({
  url,
  className,
  poster,
  onLoadStart,
  onCanPlay,
  onError
}) => {
  const [embedError, setEmbedError] = useState(false);
  const [showDirectLink, setShowDirectLink] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  // Debug logging for state changes
  useEffect(() => {
  }, [isPlaying, embedError, showDirectLink]);

  // Function to detect if URL is a YouTube video
  const isYouTubeUrl = (url: string): boolean => {
    const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)/;
    return youtubeRegex.test(url);
  };

  // Function to extract YouTube video ID
  const getYouTubeVideoId = (url: string): string | null => {
    const regexes = [
      /(?:youtube\.com\/watch\?v=)([^&\n?#]+)/,
      /(?:youtu\.be\/)([^&\n?#]+)/,
      /(?:youtube\.com\/embed\/)([^&\n?#]+)/,
      /(?:youtube\.com\/v\/)([^&\n?#]+)/
    ];

    for (const regex of regexes) {
      const match = url.match(regex);
      if (match) {
        return match[1];
      }
    }
    return null;
  };

  // Function to check if URL is Vimeo
  const isVimeoUrl = (url: string): boolean => {
    return /(?:vimeo\.com\/)(\d+)/.test(url);
  };

  // Function to extract Vimeo video ID
  const getVimeoVideoId = (url: string): string | null => {
    const match = url.match(/(?:vimeo\.com\/)(\d+)/);
    return match ? match[1] : null;
  };

  // Function to check if URL is a direct video file
  const isDirectVideoUrl = (url: string): boolean => {
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.wmv'];
    const urlLower = url.toLowerCase();
    return videoExtensions.some(ext => urlLower.includes(ext)) || url.includes('supabase.co/storage');
  };

  useEffect(() => {
    // Reset playing state when URL changes
    setIsPlaying(false);
    setEmbedError(false);
    setShowDirectLink(false);

    // For YouTube videos with click-to-play, immediately signal ready (thumbnail is static)
    if (isYouTubeUrl(url)) {
      // Small delay to ensure state updates have propagated
      const timer = setTimeout(() => {
        onCanPlay?.();
      }, 100);
      return () => clearTimeout(timer);
    }

    // For Vimeo embedded videos, trigger load callbacks
    if (isVimeoUrl(url)) {
      onLoadStart?.();
      const timer = setTimeout(() => {
        if (!embedError) {
          onCanPlay?.();
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]); // Only depend on URL, not on callback functions

  // Handle iframe load error
  const handleIframeError = () => {
    setEmbedError(true);
    setShowDirectLink(true);
    onError?.();
  };

  // Handle YouTube videos with click-to-play pattern to avoid ad blocker issues
  if (isYouTubeUrl(url)) {
    const videoId = getYouTubeVideoId(url);
    if (videoId) {
      // Use custom poster if provided, otherwise use YouTube thumbnail URLs
      const customThumbnail = poster;
      const youtubeThumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
      const fallbackThumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
      
      // Determine which thumbnail to use
      const thumbnailUrl = customThumbnail || youtubeThumbnailUrl;

      if (embedError || showDirectLink) {
        return (
          <div className={`${className} bg-gray-900 flex items-center justify-center text-white`}>
            <div className="text-center p-8">
              <div className="w-20 h-20 mx-auto mb-4 bg-red-500/20 rounded-full flex items-center justify-center">
                <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-lg mb-2">YouTube video blocked</div>
              <div className="text-sm text-gray-400 mb-4">
                Ad blockers may prevent embedded videos from loading
              </div>
              <div className="space-y-2">
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg transition-colors"
                >
                  Watch on YouTube
                </a>
                <button
                  onClick={() => {
                    setEmbedError(false);
                    setShowDirectLink(false);
                    setIsPlaying(false);
                  }}
                  className="block w-full bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        );
      }

      // Show thumbnail with play button until user clicks (avoids ad blocker blocking)
      if (!isPlaying) {
        return (
          <div
            className={className}
            style={{ position: 'relative' }}
            data-video-id={videoId}
          >
            {/* Custom or YouTube Thumbnail */}
            <img
              src={thumbnailUrl}
              alt="Video thumbnail"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                minHeight: '315px'
              }}
              onError={(e) => {
                // If custom thumbnail fails, try YouTube thumbnail
                if (customThumbnail && (e.target as HTMLImageElement).src === customThumbnail) {
                  (e.target as HTMLImageElement).src = youtubeThumbnailUrl;
                } else {
                  // Fallback to lower quality YouTube thumbnail if maxres doesn't exist
                  (e.target as HTMLImageElement).src = fallbackThumbnailUrl;
                }
              }}
            />
            {/* Dark overlay */}
            <div
              style={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s',
                zIndex: 10,
                cursor: 'pointer'
              }}
              className="hover:bg-black/20"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsPlaying(true);
                onLoadStart?.();
                // Simulate iframe loading for YouTube - call onCanPlay after a short delay
                setTimeout(() => {
                  onCanPlay?.();
                }, 1000);
              }}
            >
              {/* YouTube Play Button */}
              <div
                style={{
                  width: '68px',
                  height: '48px',
                  backgroundColor: 'rgba(255, 0, 0, 0.9)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'transform 0.2s, background-color 0.2s'
                }}
                className="hover:scale-110 hover:bg-red-600"
              >
                <svg
                  style={{ width: '24px', height: '24px', marginLeft: '4px' }}
                  fill="white"
                  viewBox="0 0 24 24"
                >
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </div>
            </div>
            {/* Click to play hint */}
            <div
              style={{
                position: 'absolute',
                bottom: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '4px 12px',
                borderRadius: '4px',
                fontSize: '12px'
              }}
            >
              Click to play
            </div>
          </div>
        );
      }

      // Use standard youtube.com embed (youtube-nocookie.com can cause issues in some cases)
      const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1`;

      return (
        <div
          className={className}
          style={{ position: 'relative' }}
          data-video-id={videoId}
        >
          <iframe
            src={embedUrl}
            title="YouTube Video"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="w-full h-full min-h-[250px] sm:min-h-[315px] md:min-h-[400px] lg:min-h-[450px]"
            onError={(e) => {
              handleIframeError();
            }}
            onLoad={() => {
              onCanPlay?.();
            }}
          />
          {/* Backup link overlay */}
          <div
            style={{
              position: 'absolute',
              bottom: '10px',
              right: '10px',
              opacity: 0.8
            }}
          >
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs bg-black/70 text-white px-2 py-1 rounded hover:bg-black/90 transition-colors"
            >
              Open in YouTube
            </a>
          </div>
        </div>
      );
    }
  }

  // Handle Vimeo videos
  if (isVimeoUrl(url)) {
    const videoId = getVimeoVideoId(url);
    if (videoId) {
      const embedUrl = `https://player.vimeo.com/video/${videoId}?badge=0&autopause=0&player_id=0&app_id=58479`;
      return (
        <div className={className} style={{ position: 'relative' }}>
          <iframe
            src={embedUrl}
            title="Vimeo Video"
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            className="w-full h-full min-h-[250px] sm:min-h-[315px] md:min-h-[400px] lg:min-h-[450px]"
          />
          <div 
            style={{
              position: 'absolute',
              bottom: '10px',
              right: '10px',
              opacity: 0.7
            }}
          >
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs bg-black/50 text-white px-2 py-1 rounded hover:bg-black/70 transition-colors"
            >
              Open in Vimeo
            </a>
          </div>
        </div>
      );
    }
  }

  // Handle direct video files (mp4, webm, etc.)
  if (isDirectVideoUrl(url)) {
    return (
      <video
        className={className}
        controls
        preload="metadata"
        poster={poster}
        crossOrigin="anonymous"
        playsInline
        onLoadStart={onLoadStart}
        onCanPlay={onCanPlay}
        onError={onError}
      >
        <source src={url} type="video/mp4" />
        <source src={url.replace('.mp4', '.webm')} type="video/webm" />
        Your browser does not support the video tag.
      </video>
    );
  }

  // Fallback for unknown URL types
  return (
    <div className={`${className} bg-gray-900 flex items-center justify-center text-white`}>
      <div className="text-center">
        <div className="text-lg mb-2">Unsupported video format</div>
        <a 
          href={url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-400 hover:text-blue-300 underline"
        >
          Open video in new tab
        </a>
      </div>
    </div>
  );
};

export default VideoPlayer;